import { useMemo } from 'react';
import {
  Menu,
  MenuList,
  Button,
  MenuItemOption,
  MenuOptionGroup,
  MenuButton,
} from '@chakra-ui/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { trioUITheme } from '@/styles/theme';
import { ValuesWithLabel } from '@/types';
import * as Styles from './Dropdown.styles';
import WarningMessage from '../WarningMessage';

export type DropDownProps = {
  label?: string;
  name: string;
  defaultValue?: string | string[];
  onChangeDropDown?: (e) => void;
  options?: ValuesWithLabel[] | string[];
  optionsWithLabel?: ValuesWithLabel[];
  isMultiple?: boolean;
  errorMessage?: string;
  isDisabled?: boolean;
  isBigger?: boolean;
  darkerLabel?: boolean;
  isLabelAboveField?: boolean;
  placeholder?: string;
  hideErrorMessage?: boolean;
  className?: string;
};

const DropDown = ({
  label,
  name,
  defaultValue,
  onChangeDropDown,
  optionsWithLabel,
  options,
  isMultiple,
  errorMessage,
  isDisabled,
  isBigger,
  darkerLabel,
  isLabelAboveField,
  hideErrorMessage = false,
  placeholder = 'Select an option',
  ...props
}: DropDownProps): JSX.Element => {
  const dropdownOptions = useMemo(() => {
    return optionsWithLabel ?? options ?? [];
  }, [options, optionsWithLabel]);

  const formattedValue = useMemo(() => {
    const isTalentPool = name === 'isTalentPool';

    const filterOptions = ({ value }): boolean => {
      if (typeof defaultValue === 'string') {
        return defaultValue === value.toString();
      }

      return defaultValue?.includes(value) || false;
    };

    const formatOptions = (option: any): string => option?.label ?? '';

    if (optionsWithLabel && !isTalentPool) {
      const filteredOptions = optionsWithLabel.filter(filterOptions);
      const formattedOptions = filteredOptions.map(formatOptions);
      return formattedOptions.join(', ');
    }

    if (typeof defaultValue === 'string') {
      return defaultValue;
    }

    if (typeof defaultValue === 'boolean') {
      return defaultValue === true ? 'Yes' : 'No';
    }

    const valuesFormatted = defaultValue?.filter(Boolean).join(', ');

    return valuesFormatted;
  }, [defaultValue, name, optionsWithLabel]);

  return (
    <Styles.InputBox
      isLabelAboveField={isLabelAboveField}
      data-testid={`dropdown-${name}`}
      hideErrorMessage={hideErrorMessage}
      {...props}
    >
      {label && (
        <Styles.InputLabel
          darkerLabel={darkerLabel}
          htmlFor={name}
          isLabelAboveField={isLabelAboveField}
        >
          {label}
        </Styles.InputLabel>
      )}
      <Styles.InputArea id="dropdown-area" isLabelAboveField={isLabelAboveField}>
        <Menu closeOnSelect={!isMultiple}>
          {({ isOpen }) => (
            <div className={`dropdown-is-${isOpen ? 'opened' : 'closed'}`}>
              <MenuButton
                className="dropdown-button"
                height={isBigger ? '42px' : '34px'}
                width="100%"
                disabled={isDisabled}
                isDisabled={isDisabled}
                textAlign="left"
                borderRadius={4}
                borderWidth={1}
                borderColor={errorMessage ? '#EF5B5B' : trioUITheme.colors['neutral-40']}
                fontWeight="normal"
                padding="5.5px 12px"
                backgroundColor="#EBEBEB"
                data-testid={`dropdown-${name}-btn`}
                rightIcon={
                  <ExpandMoreIcon
                    style={{ fill: trioUITheme.colors['neutral-100'], fontSize: '12px' }}
                  />
                }
                as={Button}
              >
                {formattedValue || placeholder}
              </MenuButton>

              <MenuList
                zIndex="3"
                minW="100%"
                w="200%"
                borderWidth={1}
                borderColor={trioUITheme.colors['neutral-40']}
                maxH={300}
                sx={{
                  overflowY: 'scroll',
                  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.07)',
                  zIndex: 2,
                  position: 'relative',
                }}
              >
                <MenuOptionGroup
                  value={defaultValue}
                  onChange={(value) => {
                    return onChangeDropDown?.({
                      target: { value, id: name, name },
                      type: 'change',
                    });
                  }}
                  type={isMultiple ? 'checkbox' : 'radio'}
                >
                  {dropdownOptions.map((option, index) => {
                    const optionValue = option?.value ?? option;
                    const optionLabel = option?.label ?? option;

                    return (
                      <MenuItemOption
                        data-testid={`dropdown-${name}-option-${index}`}
                        value={optionValue}
                        key={optionValue}
                        fontSize="14px"
                        lineHeight="21px"
                        backgroundColor={trioUITheme.colors['neutral-30']}
                      >
                        {optionLabel}
                      </MenuItemOption>
                    );
                  })}
                </MenuOptionGroup>
              </MenuList>
            </div>
          )}
        </Menu>
        {!hideErrorMessage && (
          <WarningMessage errorMessage={errorMessage} data-testid="error-message" />
        )}
      </Styles.InputArea>
    </Styles.InputBox>
  );
};

export default DropDown;
